/**
 * PayrollSubtab Component
 * 
 * Purpose:
 * - Allow daycare center owners to input a direct URL to their external payroll provider
 * - Provides quick access link to external payroll services
 * 
 * This is a convenience feature - the actual payroll processing
 * happens on the external platform (e.g., Gusto, ADP, Square Payroll, etc.)
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  ExternalLink, 
  Settings, 
  CheckCircle,
  Link as LinkIcon,
  Users,
  Building2,
  RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PayrollSubtab = ({ user, organization }) => {
  const [settings, setSettings] = useState({
    payrollUrl: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPayrollSettings();
  }, []);

  const fetchPayrollSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/payroll/settings`);
      if (response.data) {
        setSettings(prev => ({ ...prev, payrollUrl: response.data.payrollUrl || '' }));
      }
    } catch (error) {
      console.log('Payroll settings not found, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (settings.payrollUrl && !isValidUrl(settings.payrollUrl)) {
      toast.error('Please enter a valid URL (e.g., https://example.com)');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/payroll/settings`, settings);
      toast.success('Payroll settings saved successfully');
    } catch (error) {
      console.error('Error saving payroll settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleOpenPayrollLink = () => {
    if (settings.payrollUrl) {
      window.open(settings.payrollUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
        <span className="ml-2 text-gray-600">Loading payroll settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Large Hero Banner with Background */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-testid="payroll-hero-banner">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80")' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/95 via-purple-900/90 to-pink-900/85" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-pink-500/10 rounded-full translate-y-1/2 -translate-x-1/2" />
        <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-indigo-400/10 rounded-full" />
        
        <div className="relative z-10 p-8 md:p-12">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-white/90 text-sm font-medium">External Service Integration</span>
            </div>
            
            <div className="flex items-start gap-4 mb-6">
              <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm shadow-lg">
                <Building2 className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Payroll Service Portal</h1>
                <p className="text-white/80 text-lg">Connect to your external payroll processing provider</p>
              </div>
            </div>
            
            <p className="text-white/70 text-base mb-8 max-w-2xl">
              This portal provides a direct link to your external payroll service. Enter your payroll provider URL below 
              to quickly access your account from the dashboard.
            </p>
            
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <Label className="text-white font-semibold flex items-center gap-2 mb-4 text-lg">
                <LinkIcon className="w-5 h-5" />
                Your Payroll Service URL
              </Label>
              <p className="text-white/60 text-sm mb-4">
                Enter the direct login link to your payroll provider (Gusto, ADP, Square Payroll, QuickBooks, Paychex, etc.)
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="url"
                  value={settings.payrollUrl}
                  onChange={(e) => setSettings(prev => ({ ...prev, payrollUrl: e.target.value }))}
                  placeholder="https://app.gusto.com/your-company"
                  className="flex-1 h-12 bg-white/20 border-white/30 text-white placeholder:text-white/50 focus:bg-white/30 text-base rounded-xl"
                  data-testid="payroll-url-input"
                />
                <Button
                  onClick={handleOpenPayrollLink}
                  disabled={!settings.payrollUrl || !isValidUrl(settings.payrollUrl)}
                  className="h-12 px-8 bg-white text-indigo-600 hover:bg-white/90 font-bold text-base rounded-xl shadow-lg transition-all hover:scale-105"
                  data-testid="open-payroll-link-btn"
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Open Payroll
                </Button>
              </div>
              {settings.payrollUrl && !isValidUrl(settings.payrollUrl) && (
                <p className="text-yellow-300 text-sm mt-3">Please enter a valid URL starting with http:// or https://</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Secure Access</p>
                    <p className="text-white/60 text-xs">Direct link to your provider</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Staff Management</p>
                    <p className="text-white/60 text-xs">Handle all payroll tasks</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSaveSettings}
          disabled={saving}
          className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-8 py-3 text-lg font-semibold shadow-lg"
          data-testid="payroll-save-settings-btn"
        >
          <Settings className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save Payroll Settings'}
        </Button>
      </div>
    </div>
  );
};

export default PayrollSubtab;
